import { viewport } from '@/util/viewport';

// On phones, back (the button or the gesture) closes what's open over the page — the menu, a
// panel, a sheet, a dialog — the top one first, before it leaves the page, as phone apps do.
//
// Each overlay adds an entry to the browser's history, at the page's address, which back pops.
// The router never sees those entries come and go: this listener runs before its own and stops
// the events it handles. An overlay closed some other way ("Terminé", ✕) leaves its entry behind:
// back skips it, so the next press leaves the page as it would have.

// Entries left by an earlier load of the app belong to overlays long gone.
const LOAD = Date.now().toString(36);

let router = null;
let count = 0;
// The overlays open, the top one last.
const open = [];
// The overlay entry the browser is on, or null on a page's own entry.
let here = null;
// While going back over the entries of overlays closed since: the address to give the entry
// where it stops, and whether to go on back from there.
let skipping = null;

// An overlay entry's number (0 for one of an earlier load), or null for a page's own entry.
const entryOf = (state) => {
  if (!state || state.overlay == null) return null;
  return state.overlayLoad === LOAD ? state.overlay : 0;
};

const isOpen = (entry) => open.some((overlay) => overlay.entry === entry);

const pageHref = () => router.resolve(router.currentRoute.fullPath).href;

const onPage = () => router.resolve(window.location.pathname).route.path === router.currentRoute.path;

function onPopState(event) {
  const from = here;
  here = entryOf(event.state);
  const backwards = from !== null && (here === null || here < from);

  if (skipping) {
    event.stopImmediatePropagation();
    if (here !== null && !isOpen(here)) {
      window.history.back();
      return;
    }
    window.history.replaceState(event.state, '', skipping.href);
    const { leave } = skipping;
    skipping = null;
    if (leave) window.history.back();
    return;
  }

  // Another page: the router's to show. Landed on the entry of an overlay closed since, on down to
  // the page's own.
  if (!onPage()) {
    if (here !== null && !isOpen(here)) {
      skipping = { href: window.location.href, leave: false };
      window.history.back();
    }
    return;
  }

  // The page may have rewritten its address since the overlay opened (Opportunités' filters, the
  // inbox's view): the entry it goes back to takes the new one.
  if (open.length && backwards) {
    event.stopImmediatePropagation();
    window.history.replaceState(event.state, '', pageHref());
    open.pop().close();
    return;
  }
  if (here !== null && !isOpen(here)) {
    event.stopImmediatePropagation();
    skipping = { href: pageHref(), leave: backwards };
    window.history.back();
    return;
  }
  // Back from the entry of an overlay closed since: nothing to see happened.
  if (here === null && backwards) {
    event.stopImmediatePropagation();
    window.history.replaceState(event.state, '', pageHref());
    window.history.back();
  }
}

// Called once, as the router is made: it adds its own listener later, as the app starts.
export function watchBackButton(appRouter) {
  router = appRouter;
  here = entryOf(window.history.state);
  window.addEventListener('popstate', onPopState, true);
  // A page the router pushes is not an overlay's entry; one it replaces keeps what it was.
  router.afterEach(() =>
    setTimeout(() => {
      here = entryOf(window.history.state);
    }),
  );
}

// An overlay just opened: on a phone, back calls `close` instead of leaving the page. Returns what
// to call once it's closed, whichever way.
export function closeOnBack(close) {
  if (!router || viewport.size !== 'phone') return () => {};

  count += 1;
  const entry = count;
  open.push({ entry, close });
  const state = { ...window.history.state, overlay: entry, overlayLoad: LOAD };
  // On the entry of an overlay closed since, take it over rather than pile another on it.
  if (here !== null && !isOpen(here)) window.history.replaceState(state, '');
  else window.history.pushState(state, '');
  here = entry;

  return () => {
    const index = open.findIndex((overlay) => overlay.entry === entry);
    if (index !== -1) open.splice(index, 1);
  };
}
